import React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DEFAULT_SIMULATOR_ORDER,
  generateKioskToken,
  realPreflight,
  realCreateOrder,
  realPollStatus,
  realCancel,
  mockPreflight,
  mockCreateOrder,
  makeMockPoller,
  mockCancel,
  orderAmount,
} from "app/services/api/albaconSimulatorApi";

// Terminal screens per cashPayment.status (frontend spec §5).
const TERMINAL_SCREENS = {
  approved: { title: "✅ Platba prijatá", color: "#2C9E4B", note: "Pokračovať na doklad / koniec objednávky." },
  cancelled: { title: "Platba zrušená", color: "#6B7280", note: "Späť na výber platby." },
  timedOut: { title: "Čas na platbu vypršal", color: "#B45309", note: "Skúste znova / iná platba." },
  busy: { title: "Automat obsadený", color: "#B45309", note: "Skúste to o chvíľu." },
  shortage: { title: "⚠️ Automat nevie vydať drobné", color: "#DA1113", note: "Zavolajte obsluhu (hotovosť môže byť uviaznutá)." },
  awaitingAttendant: { title: "⚠️ Zavolajte obsluhu", color: "#DA1113", note: "" },
  error: { title: "Platba zlyhala", color: "#DA1113", note: "Skúste znova / iná platba." },
  reversalRequested: { title: "Overujeme platbu / vraciame hotovosť…", color: "#B45309", note: "" },
  failed: { title: "Chyba platby", color: "#DA1113", note: "Skúste znova / iná platba." },
  preflight_block: { title: "Nedá sa platiť hotovosťou", color: "#DA1113", note: "" },
  preflight_busy: { title: "Automat obsadený", color: "#B45309", note: "Skúste to o chvíľu." },
  preflight_error: { title: "Nedá sa platiť hotovosťou, skúste znova alebo iná metóda", color: "#DA1113", note: "" },
  order_error: { title: "Platba sa nepodarila spustiť", color: "#DA1113", note: "" },
};

const POLL_INTERVAL_MS_REAL = 5000;
const POLL_INTERVAL_MS_MOCK = 1200;

const nowTs = () => new Date().toLocaleTimeString("sk-SK", { hour12: false });

export default function AlbaconSimulator() {
  const { customerId } = useParams();

  const [configText, setConfigText] = React.useState(
    JSON.stringify(DEFAULT_SIMULATOR_ORDER, null, 2),
  );
  const [order, setOrder] = React.useState(null);
  const [configError, setConfigError] = React.useState("");

  const [mock, setMock] = React.useState(true);
  const [mockCfg, setMockCfg] = React.useState({
    preflightDecision: "allow",
    finalStatus: "approved",
    cancelResult: "200",
  });

  const [phase, setPhase] = React.useState("config"); // config|preflight|acknowledge|paying|terminal
  const [busy, setBusy] = React.useState(false);
  const [preflight, setPreflight] = React.useState(null);
  const [orderId, setOrderId] = React.useState(null);
  const [cashPayment, setCashPayment] = React.useState(null);
  const [terminal, setTerminal] = React.useState(null);
  const [countdown, setCountdown] = React.useState(null);
  const [countdownLabel, setCountdownLabel] = React.useState(null);
  const [log, setLog] = React.useState([]);

  const kioskTokenRef = React.useRef(null);
  const pollRef = React.useRef(null);
  const mockPollerRef = React.useRef(null);
  const countdownRef = React.useRef(null);

  const addLog = React.useCallback((dir, text, payload) => {
    setLog((prev) => [
      { ts: nowTs(), dir, text, payload: payload !== undefined ? JSON.stringify(payload) : "" },
      ...prev,
    ]);
  }, []);

  const stopTimers = React.useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    pollRef.current = null;
    countdownRef.current = null;
  }, []);

  React.useEffect(() => () => stopTimers(), [stopTimers]);

  const goTerminal = React.useCallback(
    (statusKey, cp) => {
      stopTimers();
      setCashPayment(cp || null);
      setTerminal(statusKey);
      setPhase("terminal");
    },
    [stopTimers],
  );

  const reset = () => {
    stopTimers();
    setPhase("config");
    setPreflight(null);
    setOrderId(null);
    setCashPayment(null);
    setTerminal(null);
    setCountdown(null);
    setBusy(false);
    kioskTokenRef.current = null;
    mockPollerRef.current = null;
  };

  // ---- flow steps ----
  const runPreflight = async (parsedOrder) => {
    setPhase("preflight");
    setBusy(true);
    const amount = orderAmount(parsedOrder);
    addLog("req", `POST /cash-preflight ${mock ? "(mock)" : ""}`, {
      amount,
      kioskUUID: parsedOrder.kioskUUID,
      language: parsedOrder.language,
    });
    try {
      const res = mock
        ? await mockPreflight(parsedOrder, mockCfg)
        : await realPreflight(kioskTokenRef.current, parsedOrder);
      addLog("res", "← 200 preflight", res?.preflight);
      setPreflight(res?.preflight || null);
      setBusy(false);
      const decision = res?.preflight?.decision;
      if (decision === "block") goTerminal("preflight_block");
      else if (decision === "busy") goTerminal("preflight_busy");
      else if (decision === "limit") setPhase("acknowledge");
      else await createOrder(parsedOrder); // allow
    } catch (err) {
      const status = err?.response?.status;
      addLog("err", `← ${status || "ERR"} preflight`, err?.response?.data || err?.message);
      setBusy(false);
      goTerminal("preflight_error");
    }
  };

  const createOrder = async (parsedOrder) => {
    setBusy(true);
    addLog("req", `POST /smartkiosk/orders ${mock ? "(mock)" : ""}`, {
      method: parsedOrder?.payment?.method,
    });
    try {
      const res = mock
        ? await mockCreateOrder(parsedOrder)
        : await realCreateOrder(kioskTokenRef.current, parsedOrder);
      addLog("res", "← 200 order", res);
      setBusy(false);
      if (!res?.success || !res?.orderId) {
        goTerminal("order_error");
        return;
      }
      setOrderId(res.orderId);
      setPhase("paying");
      startPolling(res.orderId);
    } catch (err) {
      addLog("err", `← ${err?.response?.status || "ERR"} order`, err?.response?.data || err?.message);
      setBusy(false);
      goTerminal("order_error");
    }
  };

  const startPolling = (oid) => {
    if (mock) mockPollerRef.current = makeMockPoller(mockCfg);
    const tick = async () => {
      addLog("req", `GET /cash-payment ${mock ? "(mock)" : ""}`, { orderId: oid });
      try {
        const res = mock
          ? await mockPollerRef.current()
          : await realPollStatus(kioskTokenRef.current, oid);
        const cp = res?.cashPayment;
        addLog("res", `← poll status=${cp?.status} final=${cp?.final}`, cp);
        setCashPayment(cp || null);
        if (cp?.deadlineAt) setCountdown(cp.deadlineAt);
        if (cp?.final === true) goTerminal(cp.status || "failed", cp);
      } catch (err) {
        addLog("err", `← poll error`, err?.response?.data || err?.message);
        // tolerate transient poll errors (retry next tick)
      }
    };
    tick();
    pollRef.current = setInterval(tick, mock ? POLL_INTERVAL_MS_MOCK : POLL_INTERVAL_MS_REAL);
  };

  const cancel = async () => {
    setBusy(true);
    addLog("req", `POST /cash-payment/cancel ${mock ? "(mock)" : ""}`, { orderId });
    try {
      const res = mock ? await mockCancel(mockCfg) : await realCancel(kioskTokenRef.current, orderId);
      addLog("res", "← 200 cancel", res);
      setBusy(false);
    } catch (err) {
      const status = err?.response?.status;
      addLog("err", `← ${status || "ERR"} cancel`, err?.response?.data || err?.message);
      setBusy(false);
      if (status === 409) {
        // already_approved → treat as success (D3)
        goTerminal("approved", { status: "approved", final: true });
      }
    }
  };

  const start = async () => {
    setConfigError("");
    let parsed;
    try {
      parsed = JSON.parse(configText);
    } catch (e) {
      setConfigError("Neplatný JSON: " + e.message);
      return;
    }
    setOrder(parsed);
    setLog([]);
    if (!mock) {
      setBusy(true);
      addLog("req", "POST /customer/generateToken", { customerId: parsed.customerId || customerId });
      try {
        const token = await generateKioskToken(parsed.customerId || customerId);
        kioskTokenRef.current = token;
        addLog("res", "← kiosk token získaný", { token: token ? "***" : null });
      } catch (err) {
        addLog("err", "← generateToken zlyhal", err?.response?.data || err?.message);
        setBusy(false);
        setConfigError("Nepodarilo sa získať kiosk token: " + (err?.message || ""));
        return;
      }
      setBusy(false);
    }
    await runPreflight(parsed);
  };

  // ---- countdown ----
  React.useEffect(() => {
    if (phase !== "paying" || !countdown) return;
    const iv = setInterval(() => {
      const ms = new Date(countdown).getTime() - Date.now();
      setCountdownLabel(ms > 0 ? Math.ceil(ms / 1000) : 0);
    }, 500);
    countdownRef.current = iv;
    return () => clearInterval(iv);
  }, [phase, countdown]);

  const amount = order ? orderAmount(order) : 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h4">Albacon simulátor kiosku</Typography>
        <Chip label={`zákazník ${order?.customerId || customerId}`} size="small" />
        <Chip
          label={mock ? "MOCK režim" : "REÁLNY režim"}
          color={mock ? "warning" : "success"}
          size="small"
        />
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {/* LEFT: config + flow */}
        <Paper sx={{ p: 2 }}>
          {phase === "config" && (
            <Stack spacing={2}>
              <FormControlLabel
                control={<Switch checked={mock} onChange={(e) => setMock(e.target.checked)} />}
                label="Mock režim (bez reálneho stroja)"
              />
              {mock && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    select size="small" label="Predkontrola"
                    value={mockCfg.preflightDecision}
                    onChange={(e) => setMockCfg((c) => ({ ...c, preflightDecision: e.target.value }))}
                  >
                    {["allow", "limit", "block", "busy", "error"].map((v) => (
                      <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select size="small" label="Výsledok platby"
                    value={mockCfg.finalStatus}
                    onChange={(e) => setMockCfg((c) => ({ ...c, finalStatus: e.target.value }))}
                  >
                    {["approved", "shortage", "busy", "error", "cancelled", "timedOut", "reversalRequested", "failed"].map((v) => (
                      <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select size="small" label="Cancel"
                    value={mockCfg.cancelResult}
                    onChange={(e) => setMockCfg((c) => ({ ...c, cancelResult: e.target.value }))}
                  >
                    {["200", "409"].map((v) => (
                      <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              )}
              <Typography variant="subtitle2">Objednávka (JSON) — editovateľné</Typography>
              <TextField
                multiline minRows={14} value={configText}
                onChange={(e) => setConfigText(e.target.value)}
                error={Boolean(configError)}
                helperText={configError || " "}
                sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12 } }}
              />
              <Button variant="contained" onClick={start} disabled={busy}>
                {busy ? <CircularProgress size={20} color="inherit" /> : "Načítať a spustiť predkontrolu"}
              </Button>
            </Stack>
          )}

          {phase === "preflight" && (
            <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
              <Typography>Kontrolujem hotovostný automat…</Typography>
            </Stack>
          )}

          {phase === "acknowledge" && (
            <Stack spacing={2}>
              <Typography variant="h6">Obmedzenie platby hotovosťou</Typography>
              <Typography>{preflight?.message}</Typography>
              {preflight?.maxAcceptedAmount != null && (
                <Typography variant="body2" color="text.secondary">
                  Max. bankovka: {preflight.maxAcceptedAmount} EUR
                </Typography>
              )}
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => createOrder(order)}>
                  Rozumiem, platím hotovosťou
                </Button>
                <Button onClick={reset}>Zrušiť</Button>
              </Stack>
            </Stack>
          )}

          {phase === "paying" && (
            <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
              <Typography variant="h5">Vhoďte hotovosť do automatu</Typography>
              <Typography variant="h3">{amount.toFixed(2)} EUR</Typography>
              {countdownLabel != null && (
                <Typography color="text.secondary">Zostáva {countdownLabel} s</Typography>
              )}
              <Chip label={`stav: ${cashPayment?.status || "pending"}`} />
              <Button variant="outlined" color="error" onClick={cancel} disabled={busy}>
                Zrušiť platbu
              </Button>
            </Stack>
          )}

          {phase === "terminal" && (
            <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
              <Typography variant="h5" sx={{ color: TERMINAL_SCREENS[terminal]?.color }}>
                {TERMINAL_SCREENS[terminal]?.title || terminal}
              </Typography>
              {TERMINAL_SCREENS[terminal]?.note && (
                <Typography color="text.secondary" align="center">
                  {TERMINAL_SCREENS[terminal].note}
                </Typography>
              )}
              {cashPayment?.reasonCode && (
                <Chip label={`reasonCode: ${cashPayment.reasonCode}`} size="small" />
              )}
              <Button variant="contained" onClick={reset}>Späť na začiatok</Button>
            </Stack>
          )}
        </Paper>

        {/* RIGHT: log */}
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1">Log</Typography>
            <Button size="small" onClick={() => setLog([])}>Vyčistiť</Button>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ overflow: "auto", maxHeight: 560, fontFamily: "monospace", fontSize: 12 }}>
            {log.length === 0 && (
              <Typography variant="body2" color="text.secondary">Zatiaľ žiadne volania.</Typography>
            )}
            {log.map((l, i) => (
              <Box key={i} sx={{ mb: 0.5, color: l.dir === "err" ? "#DA1113" : l.dir === "req" ? "#1565C0" : "#2C9E4B" }}>
                <span style={{ color: "#6B7280" }}>{l.ts} </span>
                {l.text}
                {l.payload && (
                  <div style={{ color: "#374151", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{l.payload}</div>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
